import { chorusLabels } from './chorusLabels';
import type { PassKind } from '@/music/performance';
import type { HoldWindow } from '@/audio/holds';

/** Where a written-out ending sits in the played form, in measure indices. */
export interface EndingMarks {
  /** The tag's first bar (the written pass of the tagged bars) and its shape. */
  tag?: { start: number; bars: number; times: number };
  /** The first added resolving bar. */
  resolveStart?: number;
}

export interface PlaybackStatusInput {
  /** The bar being played in the played form, -1 when idle. */
  barIndex: number;
  /** That bar's start beat in the played form. */
  barBeat: number;
  /** The 1-based chorus being played and how many there are. */
  pass: number;
  passes: number;
  kinds?: readonly PassKind[];
  ending?: EndingMarks;
  ritardando?: HoldWindow;
  /** The held last bar, when the ending holds it. */
  hold?: HoldWindow;
  /** The speed trainer's tempo for this chorus, when it runs. */
  trainerPct?: number | null;
}

/**
 * What the band is doing RIGHT NOW that the sheet does not say (#415): the
 * chorus's job under head/solos/out, the tag pass, the slow-down, the held
 * chord, the trainer's speed. Empty when idle or when nothing is in force,
 * so the strip only appears with something to tell.
 */
export function playbackStatus(input: PlaybackStatusInput): string[] {
  if (input.barIndex < 0) return [];
  const out: string[] = [];
  const labels = chorusLabels(input.passes, input.kinds);
  const label = labels[input.pass - 1];
  if (label && input.kinds?.some((kind) => kind !== 'written')) {
    out.push(/^\d+$/.test(label) ? `Solo ${label}` : label);
  }
  const { tag, resolveStart } = input.ending ?? {};
  const inside = (w: HoldWindow | undefined) =>
    !!w && input.barBeat >= w.start - 1e-6 && input.barBeat < w.end - 1e-6;
  if (resolveStart !== undefined && input.barIndex >= resolveStart) {
    out.push('Resolve');
  } else if (
    tag &&
    input.barIndex >= tag.start &&
    input.barIndex < tag.start + tag.bars * tag.times
  ) {
    out.push(`Tag ${Math.floor((input.barIndex - tag.start) / tag.bars) + 1} of ${tag.times}`);
  }
  if (inside(input.hold)) out.push('Hold');
  else if (inside(input.ritardando)) out.push('rit.');
  if (input.trainerPct != null) out.push(`Trainer ${input.trainerPct}%`);
  return out;
}
